import React, { useEffect } from "react";

import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "./components/protected-route";
import { AuthProvider } from "./contexts/auth-context";
import { ThemeProvider, useIsDark } from "./contexts/theme-context";
import { useTranslations } from "./hooks/use-translations";
import { AccountCreatedPage } from "./pages/account-created-page";
import { ActionsPage } from "./pages/actions-page";
import { ChangePasswordPage } from "./pages/change-password-page";
import { ConfirmAccountPage } from "./pages/confirm-account-page";
import { DashboardPage } from "./pages/dashboard-page";
import { ForgotPasswordPage } from "./pages/forgot-password-page";
import { LoginPage } from "./pages/login-page";
import { ProfilePage } from "./pages/profile-page";
import { RegisterPage } from "./pages/register-page";
import { ResendConfirmationEmailPage } from "./pages/resend-confirmation-email-page";
import { ResetPasswordPage } from "./pages/reset-password-page";

const DocumentTitle = ({ title }: { title: string }): React.JSX.Element | null => {
  useEffect(() => {
    if (typeof document !== `undefined`) {
      document.title = title;
    }
  }, [title]);

  return null;
};

const AppShell = (): React.JSX.Element => {
  const isDark = useIsDark();
  const { translations } = useTranslations();

  let wrapperClass = `min-h-screen`;

  if (isDark) {
    wrapperClass = `${wrapperClass} bg-gray-900`;
  }

  if (!isDark) {
    wrapperClass = `${wrapperClass} bg-white`;
  }

  return (
    <div className={wrapperClass}>
      <DocumentTitle title={translations.common.appTitle} />
      <Routes>
        {/* Public routes (must not trigger /api/account/data) */}
        <Route element={<LoginPage />} path="/login" />
        <Route element={<RegisterPage />} path="/register" />
        <Route element={<ForgotPasswordPage />} path="/forgot-password" />
        <Route element={<ResetPasswordPage />} path="/reset-password" />
        <Route element={<ConfirmAccountPage />} path="/confirm-account" />
        <Route element={<ActionsPage />} path="/actions/*" />
        <Route element={<AccountCreatedPage />} path="/account-created" />
        <Route element={<ResendConfirmationEmailPage />} path="/resend-confirmation-email" />

        {/* Protected area under /account only */}
        <Route
          path="/account/data"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/change-password"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route element={<Navigate replace to="/account/data" />} path="/account" />
        <Route element={<Navigate replace to="/login" />} path="/" />
        <Route element={<Navigate replace to="/login" />} path="*" />
      </Routes>
    </div>
  );
};

export const App = (): React.JSX.Element => (
  <ThemeProvider>
    <Router>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </Router>
  </ThemeProvider>
);
